// Copyright (c) Docugami, Inc. All rights reserved.

import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useServiceNotificationMarkAsRead } from "hooks/ServiceNotificationMarkAsRead";
import { InteractionStates, NotificationModel, WorkPathCtaContext } from "networking/NotificationsApi";
import { DocSetCollectionType, WorkPathType } from "networking/WorkPathApi";
import { NotificationItemClickedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { NotificationItem, NotificationItemProps } from "./NotificationItem";

interface WorkPathCtaNotificationProps {
    model: NotificationModel;
    context: WorkPathCtaContext;
}

export const WorkPathCtaNotification = (props: WorkPathCtaNotificationProps): JSX.Element | null => {
    const navigationService = useNavigationService();
    const markAsRead = useServiceNotificationMarkAsRead();

    // Create a new work path with the appropriate context and navigate to it
    function primaryAction() {
        Telemetry.trackEvent<NotificationItemClickedSchema>({
            id: props.model.id,
            category: props.model.category,
            type: props.model.notificationType,
            eventName: TelemetryEvent.NotificationItemClicked
        })
        markAsRead([props.model.id], true);

        const collectionFilter = props.context.useSampleDocSets ? DocSetCollectionType.Sample : DocSetCollectionType.All;
        navigationService.navToNewWorkPathPage(NavigationType.PushNewPage, props.context.workPathType, undefined, collectionFilter);
    }

    const message = getMessageForWorkPathType(props.context.workPathType, props.context.useSampleDocSets);

    const itemProps: NotificationItemProps = {
        primaryAction: { action: primaryAction, label: 'View' },
        message: message,
        createdAt: props.model.createdAt,
        isRead: !!(props.model.interactions & InteractionStates.Read)
    }

    return !!message ? <NotificationItem {...itemProps} /> : null
}

function getMessageForWorkPathType(type: WorkPathType, useSample: boolean) {
    switch (type) {
        case WorkPathType.Abstract:
            return useSample ?
                "Explore creating automated abstracts with our sample doc sets" :
                "Generate automated abstracts";

        case WorkPathType.TabularReport:
            return useSample ?
                "Explore extracting data from our sample doc sets" :
                "Extract data from your documents";

        case WorkPathType.ExcelExport:
            return useSample ?
                "Explore exporting data from our sample doc sets to Excel" :
                "Export data from your documents to Excel";

        case WorkPathType.AssistedAuthoring:
            return useSample ?
                "Try authoring new documents using our sample doc sets" :
                "Try authoring new documents using your doc sets";

        default:
            throw new Error(`Unsupported work path type: ${type}`);
    }
}
